import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, KeyRound, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { CouponCounters } from "@/components/admission/CouponBox";

const STATUS = { active: "bg-emerald-100 text-emerald-700", reserved: "bg-amber-100 text-amber-700", used: "bg-slate-800 text-white", expired: "bg-slate-100 text-slate-500" };

const ShareSetting = ({ section, value, auth, onSaved }) => {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  const save = async () => {
    try { await api.put(`/admin/coupons/settings/${section}`, { share70: Number(v) }, auth); toast.success(`Section ${section}: ${v}% of new codes will be 70% off`); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || "Could not save"); }
  };
  return (
    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-3" data-testid={`share70-${section}`}>
      <span className="text-sm font-bold text-[#0A192F] w-24">Section {section}</span>
      <Input type="number" min="0" max="100" value={v} onChange={(e) => setV(e.target.value)} className="w-20 h-9" data-testid={`share70-input-${section}`} />
      <span className="text-xs text-slate-500">% get 70% off</span>
      <Button size="sm" onClick={save} className="ml-auto bg-[#0A192F] text-white" data-testid={`share70-save-${section}`}>Save</Button>
    </div>
  );
};

export const CouponsAdmin = ({ auth, onErr }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/coupons/overview", auth).then((r) => setD(r.data)).catch(onErr), [auth, onErr]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  if (!d) return <Loader2 className="w-6 h-6 animate-spin mt-8" />;
  return (
    <div className="mt-6 space-y-5" data-testid="coupons-admin">
      <div className="flex items-center justify-between"><h3 className="font-bold text-[#0A192F]">Live coupon counters · offer ends {new Date(d.offer_end).toLocaleDateString("en-IN")}</h3>
        <Button variant="outline" size="sm" onClick={load} data-testid="coupons-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button></div>
      <CouponCounters stats={{ sections: d.sections }} />
      <div className="grid md:grid-cols-3 gap-3">{Object.entries(d.share70).map(([s, v]) => <ShareSetting key={s} section={s} value={v} auth={auth} onSaved={load} />)}</div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="coupons-table">
          <thead className="bg-[#F0F4F8] text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Code", "Discount", "Status", "Email", "Class", "Issued", "Expires"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>{d.recent.map((c) => (
            <tr key={c.code} className="border-t border-slate-100" data-testid={`coupon-row-${c.code}`}>
              <td className="px-3 py-2 font-black font-['Outfit']">{c.code}</td><td className="px-3 py-2">{c.discount_pct}%</td>
              <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${STATUS[c.status]}`}>{c.status}</span></td>
              <td className="px-3 py-2">{c.email}</td><td className="px-3 py-2">{c.class_grade}</td>
              <td className="px-3 py-2 whitespace-nowrap">{new Date(c.created_at).toLocaleString("en-IN")}</td><td className="px-3 py-2 whitespace-nowrap">{new Date(c.expires_at).toLocaleString("en-IN")}</td>
            </tr>))}
            {!d.recent.length && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">No coupons issued yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const AuditLog = ({ auth, onErr }) => {
  const [items, setItems] = useState([]);
  const [action, setAction] = useState("");
  const load = useCallback(() => api.get("/admin/audit", { ...auth, params: action ? { action } : {} }).then((r) => setItems(r.data.items)).catch(onErr), [auth, onErr, action]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="mt-6 space-y-3" data-testid="audit-log">
      <div className="flex gap-2 flex-wrap">{["", "coupon", "payment", "credentials", "referral", "school", "auth"].map((a) => (
        <button key={a || "all"} onClick={() => setAction(a)} data-testid={`audit-filter-${a || "all"}`} className={`px-3 py-1.5 rounded-full text-xs font-bold ${action === a ? "bg-[#0A192F] text-white" : "bg-white border border-slate-200"}`}>{a || "all"}</button>))}
        <Button variant="outline" size="sm" onClick={load} className="ml-auto" data-testid="audit-refresh"><RefreshCw className="w-4 h-4" /></Button></div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-[#F0F4F8] text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["When", "Action", "By", "Details"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>{items.map((a) => (
            <tr key={a.id} className="border-t border-slate-100 align-top" data-testid={`audit-row-${a.id}`}>
              <td className="px-3 py-2 whitespace-nowrap">{new Date(a.at).toLocaleString("en-IN")}</td><td className="px-3 py-2 font-bold">{a.action}</td>
              <td className="px-3 py-2">{a.actor}</td><td className="px-3 py-2 text-xs text-slate-600 font-mono break-all">{Object.entries(a.details || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</td>
            </tr>))}
            {!items.length && <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-500">No entries</td></tr>}
          </tbody></table>
      </div>
    </div>
  );
};

export const CredentialsButton = ({ row, auth }) => {
  const [busy, setBusy] = useState(false);
  if (row.payment_status !== "paid") return <span className="text-xs text-slate-400">—</span>;
  const send = async () => {
    if (!window.confirm(`Reset the temporary password for ${row.student_name} and resend login details?`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/enrollments/${row.id}/credentials`, {}, auth); toast.success(`Login ${data.login_id} sent${data.email_sent ? " by email" : " (email paused)"}${data.whatsapp ? ", WhatsApp" : ""}${data.sms ? ", SMS" : ""}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <span className="font-mono text-xs" data-testid={`login-id-${row.id}`}>{row.credentials?.login_id || "—"}</span>
      <Button size="sm" variant="outline" onClick={send} disabled={busy} className="h-7 px-2" data-testid={`resend-login-${row.id}`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <KeyRound className="w-3 h-3" />}</Button>
    </div>
  );
};
